// PlanifierPoseModal.js
import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import AuthContext from '../../../contexts/authContext';
import CarSelector from './interventionDetailsComponents/CarSelector';
import { postAPI } from '../../../apis/backend/apiNode';
import Icon from '../../../components/icon/Icon';
import showNotification from '../../../components/extras/showNotification';
import SingleAttahcement from 'components/Attachements/SingleAttahcement';
import DisplayHEICImage from 'components/Attachements/DisplayHEICImage';
import { INTERVENTION_MOTIFS, INTERVENTION_TYPES, loadCars } from '../hooks/planningService';
import CompanieContext from 'contexts/companieContext';

const DupliquerIntervention = ({
    show,
    onHide,
    intervention,
    onPoseCreated,
    reloadData
}) => {
    const [cars, setCars] = useState([]);
    const [loadingCars, setLoadingCars] = useState(false);
    const { userData } = useContext(AuthContext);
    const { companies } = useContext(CompanieContext);
    const [allAvailableAttachments, setAllAvailableAttachments] = useState([]);
    // 2. Form Data (Selected items only)
    const [poseFormData, setPoseFormData] = useState({
        date_debut: '',
        date_fin: '',
        selectedCars: [],
        notes: "",
        type: '', // Added
        motif: intervention?.motif, // Added
        attachments: [] // This is your "Selected" array
    });

    const [creatingPose, setCreatingPose] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [createdPose, setCreatedPose] = useState(null);

    // Sync initial images from intervention to 'All' and 'Selected'


    // Toggle Logic
    const handleToggleAttachment = (file) => {
        const fileId = file._id || file.id;
        setPoseFormData(prev => {
            const isSelected = prev.attachments.some(item => (item._id || item.id) === fileId);
            return {
                ...prev,
                attachments: isSelected
                    ? prev.attachments.filter(item => (item._id || item.id) !== fileId)
                    : [...prev.attachments, file]
            };
        });
    };

    const handleSelectAll = () => {
        setPoseFormData(prev => ({ ...prev, attachments: [...allAvailableAttachments] }));
    };

    const handleDeselectAll = () => {
        setPoseFormData(prev => ({ ...prev, attachments: [] }));
    };
    const handleFormChange = (field, value) => {
        setPoseFormData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };

    const handleToggleCar = (carId) => {
        setPoseFormData(prev => ({
            ...prev,
            selectedCars: prev.selectedCars.includes(carId)
                ? prev.selectedCars.filter(id => id !== carId)
                : [...prev.selectedCars, carId]
        }));
    };

    const validateForm = () => {
        if (!poseFormData.date_debut || !poseFormData.date_fin) {
            setError('Les dates de début et de fin sont requises');
            return false;
        }

        if (new Date(poseFormData.date_debut) >= new Date(poseFormData.date_fin)) {
            setError('La date de début doit être antérieure à la date de fin');
            return false;
        }

        if (poseFormData.selectedCars.length === 0) {
            setError('Au moins un véhicule doit être sélectionné');
            return false;
        }

        if (!poseFormData.type) {
            setError("Le type d'intervention est requis");
            return false;
        }
        if (!poseFormData.motif) {
            setError("Le motif d'intervention est requis");
            return false;
        }

        return true;
    };

    const handleSubmitPose = async () => {
        if (!validateForm()) return;

        try {
            setCreatingPose(true);
            setError(null);
            setSuccess(false);

            const response = await postAPI(
                'planning',
                {
                    ...poseFormData, // Includes type, motif, etc.
                    userId: userData._id || userData.id,
                    parentInterventionId: intervention._id,
                    cars: poseFormData.selectedCars,
                },
                `/intervention/${intervention._id}/dupliquer-intervention`
            );

            console.log('API Response:', response); // Ajoutez ce log pour debug

            // CORRECTION ICI : Utiliser response.success au lieu de response.data?.success
            if (response?.success) {
                // Mettre à jour l'état de succès
                setSuccess(true);
                setCreatedPose(response.data);

                // Afficher la notification de succès
                showNotification(
                    'Succès',
                    `Intervention ${response.data?.reference || ''} créée avec succès`,
                    'success'
                );

                // Rafraîchir les données
                if (reloadData) reloadData();
                if (onPoseCreated) onPoseCreated(response.data);

                // Réinitialiser le formulaire
                setPoseFormData({
                    date_debut: '',
                    date_fin: '',
                    selectedCars: [],
                    notes: '',
                    type: '', // Added
                    motif: '', // Added
                });

                // Fermer le modal après un délai (optionnel)
                setTimeout(() => {
                    onHide();
                    setSuccess(false);
                    setCreatedPose(null);
                }, 2000);

            } else {
                // Gérer les cas où success est false
                const errorMessage = response?.message || 'La création de la pose a échoué';
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('Erreur lors de la planification de la pose:', error);

            // Gérer l'erreur spécifique
            const errorMessage = error.response?.data?.message ||
                error.response?.message ||
                error.message ||
                'Erreur lors de la création de la pose';

            setError(errorMessage);

            // Afficher la notification d'erreur
            showNotification(
                'Erreur',
                errorMessage,
                'danger'
            );
        } finally {
            setCreatingPose(false);
        }
    };

    const handleClose = () => {
        // Réinitialiser tous les états
        setPoseFormData({
            date_debut: '',
            date_fin: '',
            selectedCars: [],
            notes: '',
            type: '', // Added
            motif: '', // Added
        });
        setError(null);
        setSuccess(false);
        setCreatedPose(null);
        onHide();
    };

    // Formater la date pour l'affichage
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const fetchCars = async () => {
        if (!show) return; // Only fetch when modal is shown

        setLoadingCars(true);
        try {
            const carData = await loadCars({}, companies);
            setCars(carData || []);
        } catch (error) {
            console.error('Error loading cars:', error);
            setCars([]);
        } finally {
            setLoadingCars(false);
        }
    };
    useEffect(() => {
        const syncAllData = async () => {
            if (!intervention) return;

            // 1. Gather immediate media from rapport
            const rapportAttachments = intervention?.rapport_intervention?.attachments || [];
            const responsableAttachments = intervention?.attachments || [];

            // 2. Identify all Devis objects (from rapport AND chronologie)
            const devisFromRapport = intervention?.rapport_intervention?.devis || [];
            const devisFromChronologie = intervention?.chronologie?.entries
                ?.filter(entry => entry.type === "devis")
                ?.map(entry => entry.devis) || [];

            // Merge devis and remove duplicates by ID to avoid double-fetching
            const allDevisObjects = [...devisFromRapport, ...devisFromChronologie];
            const uniqueDevisObjects = Array.from(new Map(allDevisObjects.map(d => [d.id || d._id, d])).values());
            console.log("devisFromChronologie****", allDevisObjects, uniqueDevisObjects)

            try {
                // 3. Fetch PDF Blobs for all unique devis
                const downloadPromises = uniqueDevisObjects.map(async (devis) => {
                    try {
                        const devisId = devis.id || devis._id;
                        const pdfUrl = `/document-layouts/download/Devis/${devisId}`;
                        const response = await fetch(pdfUrl);
                        if (!response.ok) throw new Error('PDF fail');
                        const blob = await response.blob();

                        return {
                            originalName: `Devis_${devis.reference || 'document'}.pdf`,
                            fileName: `Devis_${devis.reference || 'document'}.pdf`,
                            mimeType: 'application/pdf',
                            size: blob.size,
                            path: pdfUrl,
                            id: devisId,
                            _id: devisId,
                        };
                    } catch (err) {
                        console.error("Failed to fetch devis PDF", err);
                        return null;
                    }
                });

                const fetchedPdfs = (await Promise.all(downloadPromises)).filter(res => res !== null);

                // 4. Final Sync
                const totalAvailable = [...responsableAttachments, ...rapportAttachments, ...fetchedPdfs];

                setAllAvailableAttachments(totalAvailable);

                // We overwrite attachments here to ensure that when a new intervention loads, 
                // the selection is reset to "All" for that specific intervention.
                setPoseFormData(prev => ({
                    ...prev,
                    attachments: totalAvailable,
                }));

            } catch (error) {
                console.error("Critical error in syncAllData:", error);
            }
        };
        fetchCars()
        syncAllData();
    }, [intervention]);

    console.log("poseFormData****", poseFormData)

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl" // Increased to XL for the side-by-side layout
            centered
            backdrop={success ? 'static' : true}
        >
            <Modal.Header closeButton={!success} className="border-0 bg-light">
                <Modal.Title className={`${success ? 'text-success' : 'text-primary'}`}>
                    <Icon icon="CalendarPlus" className="me-2" />
                    {success ? 'Intervention Planifiée !' : 'Créer une nouvelle intervention'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ minHeight: '600px' }}>
                {/* Success & Error Alerts at the top */}
                {success && createdPose && (
                    <Alert variant="success" className="border-0 shadow-sm mb-3">
                        <div className="d-flex align-items-center">
                            <Icon icon="CheckCircle" className="me-2 fs-4" />
                            <strong>Intervention {createdPose.reference} créée avec succès !</strong>
                        </div>
                    </Alert>
                )}

                {error && !success && (
                    <Alert variant="danger" className="py-2 border-0 shadow-sm mb-3">
                        <Icon icon="Error" className="me-2" />
                        <small>{error}</small>
                    </Alert>
                )}

                {!success && (
                    <Row>
                        {/* --- LEFT COLUMN: Form Fields --- */}
                        <Col lg={6} className="border-end">
                            <div className="pe-2">
                                <div className="bg-light p-2 rounded mb-3 d-flex justify-content-between align-items-center">
                                    <span className="small fw-bold">Client: {intervention?.client?.nom}</span>
                                    <Badge bg="info">{intervention?.reference}</Badge>
                                </div>

                                <Row className="g-2 mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Date de début *</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                size="sm"
                                                value={poseFormData.date_debut}
                                                onChange={(e) => handleFormChange('date_debut', e.target.value)}
                                                isInvalid={!!error && !poseFormData.date_debut}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Date de fin *</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                size="sm"
                                                value={poseFormData.date_fin}
                                                onChange={(e) => handleFormChange('date_fin', e.target.value)}
                                                isInvalid={!!error && !poseFormData.date_fin}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-2 mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Type d'intervention *</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={poseFormData.type}
                                                onChange={(e) => handleFormChange('type', e.target.value)}
                                            >
                                                <option value="">Sélectionnez un type</option>
                                                {INTERVENTION_TYPES.map((type) => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Motif *</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={poseFormData.motif}
                                                onChange={(e) => handleFormChange('motif', e.target.value)}
                                            >
                                                <option value="">Sélectionnez un motif</option>
                                                {INTERVENTION_MOTIFS.map((motif) => (
                                                    <option key={motif.value} value={motif.value}>{motif.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group>
                                    <Form.Label>Voitures assignées *</Form.Label>
                                    {loadingCars ? (
                                        <div className="text-center p-3">
                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                                <span className="visually-hidden">Chargement...</span>
                                            </div>
                                            Chargement des voitures...
                                        </div>
                                    ) : (
                                        <>

                                            <CarSelector
                                                cars={cars}
                                                selectedCars={poseFormData.selectedCars}
                                                onToggleCar={handleToggleCar}
                                                isEditing
                                            />
                                            {cars.length === 0 && (
                                                <div className="text-muted text-center p-3">
                                                    Aucune voiture disponible
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Form.Group>


                            </div>
                        </Col>

                        {/* --- RIGHT COLUMN: Attachments --- */}
                        <Col lg={6}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Notes / Rapport</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    size="sm"
                                    value={poseFormData.notes}
                                    onChange={(e) => handleFormChange('notes', e.target.value)}
                                />
                            </Form.Group>
                            <div className="ps-2 mt-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="fw-bold mb-0 small">Pièces jointes</h6>
                                    <div className="btn-group btn-group-sm">
                                        <Button variant="outline-primary" size="sm" onClick={handleSelectAll}>Sélectionner Tout</Button>
                                        <Button variant="outline-secondary" size="sm" onClick={handleDeselectAll}>Aucun</Button>
                                    </div>
                                </div>

                                <div
                                    className="d-flex flex-wrap gap-2  "
                                    style={{ height: '420px', overflowY: 'auto', alignContent: 'flex-start' }}
                                >
                                    {allAvailableAttachments.map((file, index) => {
                                        const isSelected = poseFormData.attachments.some(
                                            (item) => (item.id || item._id) === (file.id || file._id)
                                        );
                                        return (
                                            <div
                                                key={file.id || file._id || index}
                                                onClick={() => handleToggleAttachment(file)}
                                                className="position-relative"
                                                style={{
                                                    cursor: 'pointer',
                                                    width: '135px',
                                                    height: '135px',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#fff',
                                                    border: isSelected ? '3px solid #0d6efd' : '1px solid #dee2e6'
                                                }}
                                            >
                                                <div style={{ height: '100px', backgroundColor: '#eee' }}>
                                                    <Attachements file={file} index={index} />
                                                </div>
                                                <div className="p-1 border-top bg-white">
                                                    <div className="small text-truncate fw-bold" style={{ fontSize: '10px' }}>
                                                        {file.originalName}
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <div className="position-absolute" style={{ top: 2, right: 2, background: '#0d6efd', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <span style={{ color: 'white', fontSize: '10px' }}>✓</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                        </Col>
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top bg-light">
                <Button variant="outline-secondary" onClick={handleClose} disabled={creatingPose}>
                    Annuler
                </Button>
                {!success && (
                    <Button variant="primary" onClick={handleSubmitPose} disabled={creatingPose} className="px-4">
                        {creatingPose ? 'Création...' : 'Créer l\'intervention'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default DupliquerIntervention;

function Attachements({ file, index, deleteImage = () => { } }) {
    const theFile = file;
    const fileType = theFile?.mimeType

    const src =
        "/coreService/" + theFile?.path

    if (fileType?.toLowerCase() === 'heic') {
        return (
            <DisplayHEICImage src={src} theFile={theFile} index={index} deleteImage={deleteImage} />
        );
    }

    if (/image|jpeg|jpg|png|jfif|ico/i.test(fileType)) {
        return (

            <img
                src={src}
                alt={theFile?.originalName}
                style={{
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                }}
            />

        );
    } else if (fileType?.toLowerCase().includes('video') || /quicktime|mov|mp4/i.test(fileType)) {
        return (
            <div className="w-100 h-100 bg-black d-flex align-items-center justify-content-center position-relative">
                <video
                    controls
                    muted // CRITICAL: Browsers often block videos that aren't muted
                    playsInline
                    preload="metadata"
                    style={{
                        objectFit: 'contain',
                        width: '100%',
                        height: '100%',
                    }}>
                    {/* We provide the source, but if it's HEVC, Chrome might fail */}
                    <source src={src} type={fileType} />

                    {/* Fallback UI if the browser fails to render the video */}
                    <div className="text-white text-center p-2">
                        <p className="small mb-1">Format .MOV (QuickTime)</p>
                        <a href={src} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                            Télécharger pour voir
                        </a>
                    </div>
                </video>
            </div>
        );
    } else if (
        fileType === 'application' || fileType === 'application/pdf' ||
        fileType === 'pdf'
    ) {
        return (

            <embed
                src={src}
                type='application/pdf'
                style={{
                    objectFit: 'fill',
                    width: '100%',
                    height: '100%',
                }}
            />
        );
    } else {
        return (
            <span>Fichier de format {fileType}</span>
        );
    }
}